import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlert, RefreshCw, ArrowRight, PenLine, Check, ChevronRight } from 'lucide-react';
import { api, type Contradictions as ContradictionsData } from '../lib/api';
import { usePanelOpen } from '../lib/usePanelOpen';

/**
 * « À vérifier » (CONTRA) : paires de grains qui semblent se contredire. Deux
 * actions par cas : **Clarifier** (capture pour trancher) et **Écarter** (traité).
 */
export function Contradictions({
  onCapture,
}: {
  onCapture: (
    subject: string,
    question: string,
    tag: string | undefined,
    resolve: { kind: 'contradiction'; id: string },
  ) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = usePanelOpen('gnosis.panel.contradictions', false);
  const [data, setData] = useState<ContradictionsData | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    void api.proactive
      .contradictions()
      .then(setData)
      .catch(() => setData(null));
  }, []);

  const refresh = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setRefreshing(true);
    void api.proactive
      .contradictionsRefresh()
      .then(setData)
      .catch(() => {})
      .finally(() => setRefreshing(false));
  };

  const dismiss = (id: string): void => {
    setData((d) => (d ? { ...d, items: d.items.filter((x) => x.id !== id) } : d));
    void api.proactive.dismissContradiction(id).catch(() => {});
  };

  if (!data || data.items.length === 0) return null;

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className="group rounded-2xl bg-amber-500/5 p-4 ring-1 ring-amber-500/30"
    >
      <summary className="flex cursor-pointer list-none items-center gap-2">
        <ChevronRight className="h-4 w-4 shrink-0 text-muted transition-transform group-open:rotate-90" />
        <h3 className="flex flex-1 items-center gap-1.5 text-sm font-semibold">
          <TriangleAlert className="h-4 w-4 text-amber-500" /> {t('contradictions.title')} · {data.items.length}
        </h3>
        <button
          onClick={refresh}
          disabled={refreshing}
          title={t('contradictions.refresh')}
          aria-label={t('contradictions.refresh')}
          className="grid h-7 w-7 place-items-center rounded-lg text-muted hover:bg-surface hover:text-fg disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </summary>
      <p className="mb-3 mt-1 text-xs text-muted">{t('contradictions.subtitle')}</p>
      <ul className="space-y-3">
        {data.items.map((c) => (
          <li key={c.id} className="rounded-xl bg-surface/70 p-3 ring-1 ring-border">
            <div className="mb-2 flex items-center gap-2 text-sm">
              <span className="font-medium">{c.subject}</span>
              <a href={`#/entity/${c.entityId}`} className="flex items-center gap-0.5 text-xs text-accent hover:underline">
                {t('contradictions.open')} <ArrowRight className="h-3 w-3" />
              </a>
            </div>
            <p className="mb-2 text-sm text-amber-700 dark:text-amber-300">{c.explanation}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {[c.a, c.b].map((g, j) => (
                <div key={j} className="rounded-lg bg-surface-2 px-2.5 py-2">
                  <p className="text-xs font-medium">{g.title}</p>
                  <p className="mt-0.5 text-xs text-muted">{g.excerpt}…</p>
                </div>
              ))}
            </div>
            <div className="mt-2.5 flex items-center gap-2">
              <button
                onClick={() =>
                  onCapture(c.subject, `${t('contradictions.clarifyHint')} ${c.explanation}`, undefined, {
                    kind: 'contradiction',
                    id: c.id,
                  })
                }
                className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-accent-fg hover:opacity-90"
              >
                <PenLine className="h-3.5 w-3.5" /> {t('contradictions.clarify')}
              </button>
              <button
                onClick={() => dismiss(c.id)}
                className="flex items-center gap-1.5 rounded-lg bg-surface-2 px-3 py-1.5 text-xs text-muted hover:text-fg"
              >
                <Check className="h-3.5 w-3.5" /> {t('contradictions.resolve')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}
